<template>
    <div class="relative">
        <prime-vue-progress-bar
            :mode="indeterminate ? 'indeterminate' : 'determinate'"
            :pt="pt"
            :value="displayValue"
        >
            <slot v-if="!disabled && !indeterminate && hasTemplate && (value ?? 0) >= 3" />
            <span v-else />
        </prime-vue-progress-bar>
        <span
            v-if="!disabled && !indeterminate && (value ?? 0) <= 3 && hasTemplate"
            class="absolute top-1/2 left-0 ml-1.5 -translate-y-1/2 text-xs font-semibold text-text-high"
        >
            {{ Math.floor(value!) }}%
        </span>
    </div>
</template>

<script setup lang="ts">
import {computed, type ComputedRef, type Slots, useSlots} from 'vue';
import PrimeVueProgressBar from 'primevue/progressbar';
import type {ProgressBarPassThroughOptions} from 'primevue/progressbar';

type Props = {
    value?: number;
    isHeat?: boolean;
    disabled?: boolean;
    indeterminate?: boolean;
};

const props = defineProps<Props>();
const slots: Slots = useSlots();
const hasTemplate: ComputedRef<boolean> = computed((): boolean => !!slots.default);

const level = computed(() => Math.min(Math.ceil((props.value ?? 0) / 10) * 10, 100));

const displayValue = computed(() => (props.disabled ? 100 : (props.value ?? 0)));

const fillClass = computed(() => (props.isHeat ? `bg-heat-${level.value}` : 'bg-surface-200'));

const pt = computed<ProgressBarPassThroughOptions>(() => ({
    root: () => ({
        class: [
            'relative',
            'overflow-hidden',
            'rounded-md',
            props.indeterminate
                ? ['bg-background', 'h-2']
                : [
                      props.isHeat && (props.disabled ? 'bg-surface-300' : 'bg-surface-200'),
                      props.isHeat ? `h-[20px]` : 'h-4',
                  ],
        ],
    }),
    value: () => ({
        class: props.indeterminate
            ? [
                  'absolute',
                  'w-[30%]',
                  'h-full',
                  'bg-interactives-solid-default',
                  'rounded-md',
                  'deskly-progress-bar-indeterminate',
              ]
            : [fillClass.value, 'justify-center', 'items-center', {'h-full': !props.disabled}],
    }),
    label: () => ({
        class: ['text-xs', 'font-semibold', 'text-white'],
    }),
}));
</script>

<style>
@keyframes deskly-progress-bar-indeterminate {
    0% {
        transform: translateX(-333%);
    }
    100% {
        transform: translateX(1100%);
    }
}

.deskly-progress-bar-indeterminate {
    animation: deskly-progress-bar-indeterminate 1.7s infinite ease-in-out;
}
</style>
